/** Display formatting for the console (English, en-GB dates per D-05). */

const dateFmt = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const shortFmt = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const dateTimeFmt = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const timeFmt = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });
const weekdayFmt = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'short' });

const valid = (iso: string | null | undefined): iso is string => !!iso && !Number.isNaN(new Date(iso).getTime());

export const fmtDate = (iso: string | null | undefined): string => (valid(iso) ? dateFmt.format(new Date(iso)) : '—');
export const fmtShortDate = (iso: string | null | undefined): string => (valid(iso) ? shortFmt.format(new Date(iso)) : '—');
export const fmtDateTime = (iso: string | null | undefined): string => (valid(iso) ? dateTimeFmt.format(new Date(iso)) : '—');
export const fmtTime = (iso: string | null | undefined): string => (valid(iso) ? timeFmt.format(new Date(iso)) : '—');
export const fmtToday = (): string => weekdayFmt.format(new Date());

/** "just now", "5 min ago", "3 h ago", "2 d ago", then a date. */
export function relTime(iso: string | null | undefined, now = Date.now()): string {
  if (!valid(iso)) return '—';
  const diff = now - new Date(iso).getTime();
  const future = diff < 0;
  const s = Math.abs(diff) / 1000;
  const label = s < 45 ? null : s < 3600 ? `${Math.round(s / 60)} min` : s < 86_400 ? `${Math.round(s / 3600)} h` : s < 7 * 86_400 ? `${Math.round(s / 86_400)} d` : null;
  if (label === null) return s < 45 ? 'just now' : fmtShortDate(iso);
  return future ? `in ${label}` : `${label} ago`;
}

/** Days until a date (negative when past). */
export function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

export const pct = (v: number | null | undefined, digits = 0): string =>
  v === null || v === undefined || Number.isNaN(v) ? '—' : `${(v * 100).toFixed(digits)}%`;

export function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

export function greeting(d = new Date()): string {
  const h = d.getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

export const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/** Local date input value (yyyy-mm-dd) ⇄ ISO. */
export function toDateInput(iso: string | null | undefined): string {
  if (!valid(iso)) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function fromDateInput(v: string, endOfDay = false): string {
  const [y, m, d] = v.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1, endOfDay ? 23 : 9, endOfDay ? 59 : 0).toISOString();
}
